import type {
  UpdateContractMutationOptions,
  UpdateContractVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysContract } from '../queryKeys';

import { updateContract } from './updateContract';

/**
 * Custom hook for updating an existing contract.
 * Updates contract header fields with status transition validation.
 *
 * @param options - Additional options for the mutation.
 * @returns An object containing the mutation function and related properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/update_contract_contracts__contract_id__put
 */
export const useMutationUpdateContract = (
  options?: UpdateContractMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contractId, payload }: UpdateContractVariables) =>
      updateContract(contractId, payload),
    onSuccess(_data, variables) {
      // Invalidate the specific contract query
      queryClient.invalidateQueries({
        queryKey: queryKeysContract.get(variables.contractId),
      });
      // Invalidate the contracts list
      queryClient.invalidateQueries({
        queryKey: queryKeysContract.getAll(undefined),
      });
    },
    ...options,
  });
};
